/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: VipBellLink.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  27.09.22 12:19 pm.
 * Last modified: 27.09.22 12:19 pm.
 *
 * More information at: https://egt-digital.com/
 */
import React, { useEffect } from 'react';
import { addBuildHash, getGamesMinDenomination, updateResizables } from '../../api/helpers';
import { useSettings } from '../../contexts/SettingsContext';
import { useCustomInView } from '../../hooks/useCustomInView';
import { useEffectWithRef } from '../../hooks/useEffectWithRef';
import useJackpotHoverPopup from '../../hooks/useJackpotHoverPopup';
import JackpotLevelValue from './JackpotLevelValue';

const VipBellLinkJackpot = () => {
    const [setRefLevel1, refLevel1] = useEffectWithRef(node => {
        updateResizables(node, true);
    });
    const [setRefLevel2, refLevel2] = useEffectWithRef(node => {
        updateResizables(node, true);
    });
    const [setRefLevel3, refLevel3] = useEffectWithRef(node => {
        updateResizables(node, true);
    });
    const [setRefLevel4, refLevel4] = useEffectWithRef(node => {
        updateResizables(node, true);
    });
    const [jpContainerRef, isVisible] = useCustomInView();

    const [settings] = useSettings();

    useEffect(() => {
        if (isVisible) {
            if (refLevel1.current) {
                updateResizables(refLevel1.current);
            }
            if (refLevel2.current) {
                updateResizables(refLevel2.current);
            }
            if (refLevel3.current) {
                updateResizables(refLevel3.current);
            }
            if (refLevel4.current) {
                updateResizables(refLevel4.current);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    const currency = settings.currency;
    const sectionName = 'VIP_BELL_LINK';
    const bellLinkSection = settings.sections.find(section => section.name === sectionName);
    const denomination: number = getGamesMinDenomination(bellLinkSection?.games || [], settings.currency);

    const jackpotName = 'VIP Bell Link';

    const [hoverHandlers, HoverComponent] = useJackpotHoverPopup({ jackpotName, denomination, currency });

    return (
        <div className="jp_vbl_panel">
            <div
                className="jp_vbl_values"
                style={{
                    background: `url(${addBuildHash(
                        settings.host + '/assets/portal/group_vip_bell_link/panel.png',
                    )}) center left/contain no-repeat`,
                }}
                ref={jpContainerRef}
            >
                <div className="jp_inside jp_vbl_container_level1" {...hoverHandlers(1)}>
                    <img
                        src={addBuildHash(`${settings.host}/assets/portal/group_vip_bell_link/grand.png`)}
                        id={`vbl_level1_placeholder_${sectionName}`}
                        alt=""
                    />
                    <HoverComponent level={1} classes={['jp_vbl_hover_offset_28']} arrowClass="egtd_jpw_hp_arrow_60" />
                    <div
                        className={`resizable`}
                        data-target={`vbl_level1_placeholder_${sectionName}`}
                        data-text-align="right"
                        ref={setRefLevel1}
                        data-padding-right-pixels={60}
                        data-padding-left-pixels={10}
                    >
                        <div className={`jp_vbl_counter jp_vbl_numbers jp_vbl_level1`}>
                            <JackpotLevelValue jackpotName={jackpotName} denomination={denomination} level={1} />
                            <span className="jp_vbl_currency">{currency}</span>
                        </div>
                    </div>
                </div>
                <div className="jp_inside jp_vbl_container_level2" {...hoverHandlers(2)}>
                    <img
                        src={addBuildHash(`${settings.host}/assets/portal/group_vip_bell_link/major.png`)}
                        id={`vbl_level2_placeholder_${sectionName}`}
                        alt=""
                    />
                    <HoverComponent level={2} classes={['jp_vbl_hover_offset_18']} arrowClass="egtd_jpw_hp_arrow_60" />
                    <div
                        className={`resizable`}
                        data-target={`vbl_level2_placeholder_${sectionName}`}
                        data-text-align="right"
                        ref={setRefLevel2}
                        data-padding-right-pixels={60}
                        data-padding-left-pixels={10}
                    >
                        <div className={`jp_vbl_counter jp_vbl_numbers jp_vbl_level2`}>
                            <JackpotLevelValue jackpotName={jackpotName} denomination={denomination} level={2} />
                            <span className="jp_vbl_currency">{currency}</span>
                        </div>
                    </div>
                </div>
                <div className="jp_inside jp_vbl_container_level3" {...hoverHandlers(3)}>
                    <img
                        src={addBuildHash(`${settings.host}/assets/portal/group_vip_bell_link/minor.png`)}
                        id={`vbl_level3_placeholder_${sectionName}`}
                        alt=""
                    />
                    <HoverComponent level={3} />
                    <div
                        className={`resizable`}
                        data-target={`vbl_level3_placeholder_${sectionName}`}
                        data-text-align="right"
                        ref={setRefLevel3}
                        data-padding-right-pixels={60}
                        data-padding-left-pixels={10}
                    >
                        <div className={`jp_vbl_counter jp_vbl_numbers jp_vbl_level3`}>
                            <JackpotLevelValue
                                jackpotName={jackpotName}
                                denomination={denomination}
                                level={3}
                                defaultValue={5000}
                            />
                            <span className="jp_vbl_currency">{currency}</span>
                        </div>
                    </div>
                </div>
                <div className="jp_inside jp_vbl_container_level4" {...hoverHandlers(4)}>
                    <img
                        src={addBuildHash(`${settings.host}/assets/portal/group_vip_bell_link/mini.png`)}
                        id={`vbl_level4_placeholder_${sectionName}`}
                        alt=""
                    />
                    <HoverComponent level={4} />
                    <div
                        className={`resizable`}
                        data-target={`vbl_level4_placeholder_${sectionName}`}
                        data-text-align="right"
                        ref={setRefLevel4}
                        data-padding-right-pixels={60}
                        data-padding-left-pixels={10}
                    >
                        <div className={`jp_vbl_counter jp_vbl_numbers jp_vbl_level4`}>
                            <JackpotLevelValue
                                jackpotName={jackpotName}
                                denomination={denomination}
                                level={4}
                                defaultValue={1000}
                            />
                            <span className="jp_vbl_currency">{currency}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VipBellLinkJackpot;
